import { ClassificationType } from "@/types/config";
import { Agent } from "../types/agent";
import { AssessmentInfo } from "../types/assessment";
import { UserData, UserInfo } from "../types/auth";
import { ChatMessage } from "../types/chat";
import { Course } from "../types/course";
import {
  Document,
  DocumentInfo,
  DocumentStatus,
  DocumentView,
  Group,
  SentimentType,
  Suggestion,
} from "../types/document";
import { GradeDistribution, Grading } from "../types/grading";
import { InsightsChatRequest, InsightsChatResponse } from "../types/insights";
import { Rubric } from "../types/rubric";

// Map of action types to analytics events
// These events will be automatically logged in app dispatcher
export const ANALYTICS_EVENTS: Partial<Record<AppAction["type"], string>> = {
  ADD_ASSESSMENT: "Create assessment",
  ADD_COURSE: "Create course",
  ADD_DOCUMENT: "Upload submission",
  ADD_CHAT_MESSAGE: "Post chat message",
  CREATE_SUGGESTION: "Create suggestion",
  COMPLETE_ONBOARDING: "Complete onboarding",
  DELETE_DOCUMENT: "Delete assignment",
  DELETE_SUGGESTION: "Delete suggestion",
  DELETE_ONBOARDING: "Dismiss onboarding",
  ENTER_FOCUS_MODE: "Enter focus mode",
  EXIT_FOCUS_MODE: "Exit focus mode",
  SET_CLASSIFICATION_FILTERS: "Update AI preferences",
  SET_CURRENT_DOCUMENT: "Open document",
  SET_GRADING: "View grading",
  UPDATE_FINAL_GRADE: "Update final grade",
  TOGGLE_COMMAND_MENU: "Toggle command menu",
  TOGGLE_COPILOT: "Toggle AI copilot",
  TOGGLE_KEYBOARD_SHORTCUTS: "View keyboard shortcuts",
  SHOW_REFERENCE_PANEL: "Show references panel",
  SHOW_GRADING_PANEL: "Show grading panel",
  TOGGLE_SCRATCHPAD: "Toggle scratchpad",
  UPDATE_BLOCK_SENTIMENT: "Update block sentiment",
  UPDATE_DOCUMENT_NOTES: "Update document notes",
  UPDATE_DOCUMENT_STATUS: "Update document status",
  UPDATE_FEEDBACK: "Publish feedback",
  UPDATE_SUGGESTION: "Update suggestion",
  CLEAR_CHAT_MESSAGES: "Clear chat messages",
  START_ONBOARDING: "Start onboarding",
} as const;

interface OnboardingState {
  navigate: boolean;
  highlight: boolean;
  comment: boolean;
  suggestion: boolean;
  generate: boolean;
  publish: boolean;
}

export type InsightsMode = "search" | "report";
export type InsightsPhase = "idle" | "loading" | "result";

export interface InsightsState {
  query: string;
  selectedContextValue: string;
  mode: InsightsMode;
  phase: InsightsPhase;
  result: InsightsChatResponse | null;
  error: string | null;
  lastRequest: InsightsChatRequest | null;
  sessionId: string | null;
}

export type ErrorType = "auth" | "network" | "other";

export interface AppError {
  type: ErrorType;
  message: string;
  timestamp: number;
}

export interface AppState {
  agents: Agent[] | null;
  assessments: AssessmentInfo[] | null;
  error: AppError | null;
  classificationFilters: ClassificationType[];
  chatMessages: ChatMessage[] | null;
  commandMenuVisible: boolean;
  copilotVisible: boolean;
  courses: Course[] | null;
  currentDocument: Document | null;
  currentRubric: Rubric | null;
  documents: DocumentInfo[] | null;
  documentsView: DocumentView | null;
  expandedBlockId: string | null;
  grading: Grading | null;
  gradeDistribution: GradeDistribution | null;
  gradingPanel: {
    visible: boolean;
    view: "remarks" | "distribution";
    selectedCriterionId?: string;
  };
  firstSuggestionId: string | null;
  focusMode: boolean;
  isGeneratingReport: boolean;
  isNoteEditorOpen: boolean;
  isPublishingFeedback: boolean;
  isScratchpadOpen: boolean;
  isDiscrepanciesPanelOpen: boolean;
  keyboardShortcutsVisible: boolean;
  onboarding: OnboardingState;
  referencePanel: { visible: boolean; view: "toc" | "references" | "notes" };
  selectedBlockId: string | null;
  selectedBlockIndex: number;
  selectedGroup: Group | null;
  selectedSuggestion: Suggestion | null;
  selectedSuggestionIndex: number | null;
  user: UserInfo | null;
  insights: InsightsState;
}

export type AppAction =
  | { type: "ADD_AGENT"; payload: Agent }
  | { type: "ADD_ASSESSMENT"; payload: AssessmentInfo }
  | { type: "ADD_COURSE"; payload: Course }
  | { type: "ADD_CHAT_MESSAGE"; payload: ChatMessage }
  | { type: "ADD_DOCUMENT"; payload: DocumentInfo }
  | { type: "COMPLETE_ONBOARDING" }
  | { type: "CREATE_SUGGESTION"; payload: Suggestion }
  | { type: "DELETE_AGENT"; payload: string }
  | { type: "DELETE_ASSESSMENT"; payload: string }
  | { type: "DELETE_DOCUMENT"; payload: string }
  | { type: "DELETE_ONBOARDING" }
  | { type: "DELETE_SUGGESTION"; payload: string }
  | { type: "ENTER_FOCUS_MODE" }
  | { type: "EXIT_FOCUS_MODE" }
  | { type: "CLEAR_CHAT_MESSAGES" }
  | { type: "SET_AGENTS"; payload: Agent[] }
  | { type: "SET_ASSESSMENTS"; payload: AssessmentInfo[] }
  | { type: "SET_ERROR"; payload: AppError | null }
  | { type: "CLEAR_ERROR" }
  | { type: "SET_CHAT_MESSAGES"; payload: ChatMessage[] }
  | { type: "UPDATE_CHAT_MESSAGE"; payload: ChatMessage }
  | { type: "SET_CLASSIFICATION_FILTERS"; payload: ClassificationType[] }
  | { type: "SET_COURSES"; payload: Course[] }
  | { type: "SET_CURRENT_DOCUMENT"; payload: Document | null }
  | { type: "SET_CURRENT_RUBRIC"; payload: Rubric | null }
  | { type: "SET_DOCUMENTS"; payload: DocumentInfo[] }
  | { type: "SET_DOCUMENTS_VIEW"; payload: DocumentView | null }
  | { type: "SET_EXPANDED_BLOCK_ID"; payload: string | null }
  | { type: "SET_GENERATING_REPORT"; payload: boolean }
  | { type: "SET_GRADING"; payload: Grading | null }
  | { type: "SET_GRADE_DISTRIBUTION"; payload: GradeDistribution | null }
  | { type: "SET_NOTE_EDITOR_OPEN"; payload: boolean }
  | {
      type: "SET_SELECTED_BLOCK_INDEX";
      payload: number | { index: number; fromKeyboard: boolean };
    }
  | { type: "SET_SELECTED_GROUP"; payload: Group | null }
  | { type: "SET_SELECTED_SUGGESTION"; payload: Suggestion | null }
  | { type: "SET_SELECTED_SUGGESTION_INDEX"; payload: number | null }
  | { type: "SET_USER_DATA"; payload: UserData }
  | { type: "CLEAR_SUGGESTION_SELECT_BLOCK"; payload: number }
  | { type: "TOGGLE_COMMAND_MENU" }
  | { type: "TOGGLE_COPILOT" }
  | { type: "TOGGLE_KEYBOARD_SHORTCUTS" }
  | {
      type: "SHOW_REFERENCE_PANEL";
      payload: { visible: boolean; view?: "toc" | "references" | "notes" };
    }
  | {
      type: "SHOW_GRADING_PANEL";
      payload: {
        visible: boolean;
        view?: "remarks" | "distribution";
        selectedCriterionId?: string;
      };
    }
  | { type: "TOGGLE_SCRATCHPAD" }
  | { type: "TOGGLE_DISCREPANCIES_PANEL" }
  | { type: "UPDATE_AGENT"; payload: Agent }
  | { type: "UPDATE_ASSESSMENT"; payload: AssessmentInfo }
  | { type: "UPDATE_COURSE"; payload: Partial<Course> & { id: string } }
  | {
      type: "UPDATE_BLOCK_SENTIMENT";
      payload: { blockId: string; sentiment: SentimentType | null };
    }
  | { type: "UPDATE_DOCUMENT_NOTES"; payload: { id: string; notes: string } }
  | {
      type: "UPDATE_DOCUMENT_STATUS";
      payload: { id: string; status: DocumentStatus };
    }
  | {
      type: "UPDATE_DOCUMENT_PROGRESS";
      payload: { id: string; progress: { current: number; total: number } };
    }
  | { type: "UPDATE_SUGGESTION"; payload: Suggestion }
  | { type: "UPDATE_FINAL_GRADE"; payload: string }
  | {
    type: "UPDATE_FEEDBACK";
    payload: {
      id: string;
      feedback: {
        status?: "proposed" | "saved" | "published";
        publishedAt?: string;
      };
    };
  }
  | {
      type: "SAVE_FEEDBACK";
      payload: { id: string; feedback: { summary: string; status: "saved" } };
    }
  | { type: "UPDATE_FEEDBACK_SUMMARY"; payload: string }
  | { type: "SET_IS_PUBLISHING_FEEDBACK"; payload: boolean }
  | { type: "SET_ONBOARDING"; payload: { generate: boolean } }
  | { type: "START_ONBOARDING" }
  | { type: "UPDATE_USER_NOTES"; payload: string }
  | { type: "UPDATE_USER_SETTINGS"; payload: { semesters: string[] } }
  | { type: "SET_INSIGHTS_STATE"; payload: Partial<InsightsState> }
  | { type: "RESET_INSIGHTS_STATE" };
